import { Alert, Button, Group, Modal, Stack, Text } from "@mantine/core";

type Props = {
  readonly opened: boolean;
  /** 1 = 首层风险提示；2 = 最终不可恢复确认 */
  readonly step: 1 | 2;
  readonly summary: string;
  readonly loading: boolean;
  readonly onClose: () => void;
  readonly onContinue: () => void;
  readonly onConfirmDelete: () => void;
};

/**
 * 平台凭据软删两层确认：
 * 第 1 层说明影响，点「继续删除」进入第 2 层；
 * 第 2 层再次确认后才真正发 DELETE（服务端 soft-delete，不动 click/ad 历史）。
 */
export function CredentialDeleteConfirm(props: Props) {
  const { opened, step, summary, loading, onClose, onContinue, onConfirmDelete } = props;

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={step === 1 ? "删除平台凭据（第 1/2 步）" : "最终确认删除（第 2/2 步）"}
      size="md"
      keepMounted={false}
      closeOnClickOutside={!loading}
      closeOnEscape={!loading}
    >
      <Stack gap="sm">
        <Text size="sm" fw={600}>
          {summary}
        </Text>
        {step === 1 ? (
          <>
            <Alert color="orange" variant="light" title="删除会影响上报">
              <Stack gap={4}>
                <Text size="xs">列表移除该凭据，并清除加密密钥 / Token / SA JSON。</Text>
                <Text size="xs">历史点击与已上报转化完整保留，不改 click / conversion。</Text>
                <Text size="xs">
                  已 pin 到该凭据的广告 / 点击仍保留引用；后续转化会 skip，需重新添加凭据后再绑定。
                </Text>
                <Text size="xs">
                  同 Pixel / Customer ID 可重新添加。若只是临时停用，请关「启用」开关，不要删除。
                </Text>
              </Stack>
            </Alert>
            <Text size="sm" c="dimmed">
              若只是临时停用，请改用「启用」开关，不要删除。
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose} disabled={loading}>
                取消
              </Button>
              <Button color="orange" onClick={onContinue} disabled={loading}>
                继续删除
              </Button>
            </Group>
          </>
        ) : (
          <>
            <Alert color="red" variant="light" title="密钥不可恢复">
              <Text size="xs">
                请再次确认：删除后密钥从系统清除，无法一键恢复。历史数据保留。误删需重新上传 Token /
                SA JSON，并按需重新绑定广告。
              </Text>
            </Alert>
            <Group justify="flex-end">
              <Button variant="default" onClick={onClose} disabled={loading}>
                取消
              </Button>
              <Button color="red" loading={loading} onClick={onConfirmDelete}>
                确认删除
              </Button>
            </Group>
          </>
        )}
      </Stack>
    </Modal>
  );
}
